export type MarkdownAction = 'bold' | 'italic' | 'heading' | 'bullet' | 'numbered' | 'link';

export interface MarkdownEdit {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

/** Allow http(s), mailto, and same-origin paths. Drop javascript:, data:, and protocol-relative URLs. */
export function safeMarkdownUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || /[\s]/.test(trimmed)) return '';
  if (trimmed.startsWith('/') && !trimmed.startsWith('//') && !trimmed.startsWith('/\\')) {
    return trimmed;
  }
  try {
    const parsed = new URL(trimmed);
    if (!SAFE_PROTOCOLS.has(parsed.protocol)) return '';
    return trimmed;
  } catch {
    return '';
  }
}

function clamp(value: number, max: number): number {
  return Math.max(0, Math.min(value, max));
}

function wrappedBy(value: string, start: number, end: number, marker: string): boolean {
  if (value.slice(start - marker.length, start) !== marker) return false;
  if (value.slice(end, end + marker.length) !== marker) return false;
  if (marker === '*') {
    if (value[start - marker.length - 1] === '*') return false;
    if (value[end + marker.length] === '*') return false;
  }
  return true;
}

function wrapSelection(
  value: string,
  start: number,
  end: number,
  marker: string,
  placeholder: string,
): MarkdownEdit {
  const selected = value.slice(start, end);
  if (wrappedBy(value, start, end, marker)) {
    const next =
      value.slice(0, start - marker.length) + selected + value.slice(end + marker.length);
    const selectionStart = start - marker.length;
    return {
      value: next,
      selectionStart,
      selectionEnd: selectionStart + selected.length,
    };
  }
  const inner = selected || placeholder;
  const inserted = `${marker}${inner}${marker}`;
  const next = value.slice(0, start) + inserted + value.slice(end);
  const selectionStart = start + marker.length;
  return {
    value: next,
    selectionStart,
    selectionEnd: selectionStart + inner.length,
  };
}

function lineBounds(value: string, start: number, end: number): { lineStart: number; lineEnd: number } {
  const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  const endForLine = end > start && value[end - 1] === '\n' ? end - 1 : end;
  const nextBreak = value.indexOf('\n', endForLine);
  const lineEnd = nextBreak === -1 ? value.length : nextBreak;
  return { lineStart, lineEnd };
}

function stripMarker(line: string): string {
  return line.replace(/^(?:#{1,6}\s+|[-*]\s+|\d+\.\s+)/, '');
}

function prefixLines(
  value: string,
  start: number,
  end: number,
  action: 'heading' | 'bullet' | 'numbered',
): MarkdownEdit {
  const { lineStart, lineEnd } = lineBounds(value, start, end);
  const lines = value.slice(lineStart, lineEnd).split('\n');
  const pattern =
    action === 'heading' ? /^#{1,6}\s+/ : action === 'bullet' ? /^[-*]\s+/ : /^\d+\.\s+/;
  const contentLines = lines.filter((line) => line.trim());
  const remove = contentLines.length > 0 && contentLines.every((line) => pattern.test(line));

  let number = 0;
  const nextLines = lines.map((line) => {
    if (!line.trim()) return line;
    const text = stripMarker(line);
    if (remove) return text;
    if (action === 'heading') return `## ${text || 'Heading'}`;
    if (action === 'bullet') return `- ${text}`;
    number += 1;
    return `${number}. ${text}`;
  });

  const block = nextLines.join('\n');
  return {
    value: value.slice(0, lineStart) + block + value.slice(lineEnd),
    selectionStart: lineStart,
    selectionEnd: lineStart + block.length,
  };
}

function insertLink(value: string, start: number, end: number): MarkdownEdit {
  const selected = value.slice(start, end);
  const isUrl = /^https?:\/\//i.test(selected.trim());
  const label = !selected || isUrl ? 'link text' : selected;
  const url = isUrl ? selected.trim() : 'https://';
  const inserted = `[${label}](${url})`;
  const next = value.slice(0, start) + inserted + value.slice(end);
  const selectionStart = start + label.length + 3;
  return {
    value: next,
    selectionStart,
    selectionEnd: selectionStart + url.length,
  };
}

export function applyMarkdownAction(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  action: MarkdownAction,
): MarkdownEdit {
  const start = clamp(Math.min(selectionStart, selectionEnd), value.length);
  const end = clamp(Math.max(selectionStart, selectionEnd), value.length);

  if (action === 'bold') return wrapSelection(value, start, end, '**', 'bold');
  if (action === 'italic') return wrapSelection(value, start, end, '*', 'italic');
  if (action === 'link') return insertLink(value, start, end);
  return prefixLines(value, start, end, action);
}
